export const FEATURE_NAME = "dumping";

/**
 * Waste categories an operator can attribute to a confirmed dump. Broad
 * categories (rather than specific objects) so a reviewer can classify any
 * dump from the clip without hunting for an exact match. Stored as stable enum
 * keys; the human-facing label lives beside the keys below. A free-text
 * `description` is always required on confirmation (enforced at the API
 * boundary) so every dump carries reviewer detail regardless of category.
 *
 * Declared here (rather than in the client-messages contract) so the ElectroDB
 * schema and the zod contract share a single source of truth without the
 * schema layer depending on the API layer.
 */
export const OBJECT_TYPES = [
  "general-waste",
  "furniture",
  "building-materials",
  "green-waste",
  "e-waste",
  "vehicle-waste",
  "hazardous-waste",
  "mixed-waste",
  "unknown",
  "other",
] as const;

export type ObjectType = (typeof OBJECT_TYPES)[number];

/**
 * Short, human-facing name for each {@link OBJECT_TYPES} key — the label the
 * key represents (e.g. "General Waste"). This is what the customer-facing
 * report shows and the canonical name for the category. The descriptive helper
 * text shown beside it in the capture dropdown lives in
 * {@link OBJECT_TYPE_DESCRIPTIONS}. Kept beside the keys so a new option can't
 * be added without a label, and so the dropdown ordering stays in lockstep with
 * the enum. The wire/storage layers deal in keys, not these strings.
 */
export const OBJECT_TYPE_LABELS: Record<ObjectType, string> = {
  "general-waste": "General Waste",
  furniture: "Furniture",
  "building-materials": "Building Materials",
  "green-waste": "Green Waste",
  "e-waste": "E-Waste",
  "vehicle-waste": "Vehicle Waste",
  "hazardous-waste": "Hazardous Waste",
  "mixed-waste": "Mixed Waste",
  unknown: "Unknown",
  other: "Other",
};

/**
 * Descriptive helper text for each category, shown beside the bold
 * {@link OBJECT_TYPE_LABELS} name in the capture dropdown to disambiguate
 * categories. Frontend-dropdown only — never stored and never shown on reports
 * (the report surfaces just the label name).
 */
export const OBJECT_TYPE_DESCRIPTIONS: Record<ObjectType, string> = {
  "general-waste": "Everyday rubbish",
  furniture: "Beds, sofas, tables, chairs",
  "building-materials": "Bricks, timber, concrete, plasterboard",
  "green-waste": "Branches, grass, leaves",
  "e-waste": "TVs, computers, monitors, printers",
  "vehicle-waste": "Tyres, bumpers, engines, car parts",
  "hazardous-waste": "Chemicals, paint, batteries, asbestos, gas bottles",
  "mixed-waste": "Multiple waste types together",
  unknown: "Cannot determine from the image",
  other: "Other objects not included in this list",
};
